import type { Platforms } from "@/types/platform";
import { pickTextColor, deduceBtnRounding } from "@/utils/helpers.js";
import type { NavItem } from "@/types/general";

export const usePlatformStore = defineStore(
	"platform",
	() => {
		const { getPlatformSettingsFromURLPlatform } = useECMApi();
		const { $sentry } = useNuxtApp();

		interface PlatformData {
			platform_settings: Platforms[];
			public_address: any[];
			public_contact: any;
		}

		const { mode } = useRuntimeConfig().public;
		const platform = ref<PlatformData | null>(null);
		const platformInitialized = ref(false);

		const platformName = computed(() => platform.value?.platform_settings[0]?.platform_config.name || "");
		const platformId = computed(() => platform.value?.platform_settings[0]?.id || "");
		const platformOrgId = computed(() => platform.value?.platform_settings[0]?.organization_id || "");
		const platformBaseUrl = computed(() => platform.value?.platform_settings[0]?.platform_config?.url);
		const platformDomain = computed(() => platform.value?.platform_settings[0]?.platform_config?.domain);

		const plfm = computed(() => {
			const primaryFont = platform.value?.platform_settings[0]?.platform_branding.platform_font.primary_font;
			const secondaryFont = platform.value?.platform_settings[0]?.platform_branding.platform_font.secondary_font;
			const isEditorial = platform.value?.platform_settings[0]?.platform_branding.style === "Editorial";
			const colorSet = platform.value?.platform_settings[0]?.platform_branding.platform_colorset;
			const btnBgColor =
				platform.value?.platform_settings[0]?.platform_branding?.platform_input.primary_color || "#000000";

			return {
				style: platform.value?.platform_settings[0]?.platform_branding.style,
				isEditorial,
				mainFont: primaryFont || "Lato",
				secondaryFont: isEditorial && secondaryFont ? secondaryFont : primaryFont || "Lato",
				colors: {
					text: colorSet?.standard_text || "#000",
					titles: colorSet?.titles || "#000",
					subtitles: colorSet?.subtitles || "#505055",
					links: colorSet?.links || "#0C7CE6",
				},
				btns: {
					bg: btnBgColor,
					text: pickTextColor(btnBgColor),
					rounding: deduceBtnRounding(platform.value?.platform_settings[0]?.platform_branding?.platform_input.shape),
				},
			};
		});

		const topBannerMessage = computed(
			() => platform.value?.platform_settings?.[0]?.platform_branding.custom_standard_titlebar_msg
		);

		const imgixEcmOdcUrl = computed(() => {
			return mode === "production" || mode === "staging"
				? "https://tengiva-ecm-odc-prod.imgix.net"
				: "https://tengiva-ecm-odc-dev.imgix.net";
		});

		const imgixEcmWpcUrl = computed(() => {
			const imgixEcmWpcDomain =
				mode === "production" || mode === "staging"
					? "tengiva-ecm-wpc-prod.imgix.net"
					: "tengiva-ecm-wpc-dev.imgix.net";
			return `https://${imgixEcmWpcDomain}/${platformId.value}`;
		});

		const faviconUrl = computed(
			() => `${imgixEcmWpcUrl.value}/${platform.value?.platform_settings[0]?.platform_branding.favicon_id}`
		);

		const logoUrl = computed(() => {
			const settings = platform.value?.platform_settings?.[0];
			return `${imgixEcmWpcUrl.value}/${settings?.platform_branding.logo}`;
		});

		const featureFlags = computed(() => {
			const { $featureFlags } = useNuxtApp();
			const flags = $featureFlags?.flags || {};

			return {
				translations: flags["show-translations"]?.enabled || false,
				filters: {
					enabled: flags["products-filters"]?.enabled || false,
					filters: (flags["products-filters"]?.enabled && JSON.parse(flags["products-filters"].value)) || {
						enabledFilters: [],
					},
				},
				compiledShortDescription: flags["compiled-short-descriptions"]?.enabled || false,
			};
		});

		const collections = ref<NavItem[]>([]);
		const showAboutPage = computed(() => {
			const pages = platform.value?.platform_settings[0]?.pages || [];
			return pages.filter(p => p.page_name === "About Us")[0]?.publish_state;
		});

		async function getPlatform(platformUrl: string) {
			try {
				const { response } = await getPlatformSettingsFromURLPlatform<PlatformData>(platformUrl);

				platform.value = response.value?.data;
			} catch (error) {
				$sentry?.captureException("Cannot get platform settings from url platform");
			}
		}

		// Only show megamenu for colombia textile
		const showMegaMenu = computed(() => {
			// Include Platform ids in different envs
			const colombiaTextilePlatformIds = [
				"3189aadd-3a92-4d23-a236-712d0334a154", // staging & prod
				"7c86d301-7337-470e-94d0-5d0d9256554c", // dev
			];
			return colombiaTextilePlatformIds.includes(platformId.value);
		});

		return {
			platformInitialized,
			platform,
			platformBaseUrl,
			platformDomain,
			platformName,
			platformId,
			platformOrgId,
			plfm,
			topBannerMessage,
			imgixEcmOdcUrl,
			imgixEcmWpcUrl,
			faviconUrl,
			logoUrl,
			featureFlags,
			collections,
			showAboutPage,
			getPlatform,
			showMegaMenu,
		};
	}
	// {
	// 	persist: {
	// 		storage: persistedState.sessionStorage,
	// 	},
	// }
);
